'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import Image from 'next/image'
import { TopBar, BottomNav } from '@/components/layout/navigation'
import { useCartStore } from '@/store/cart-store'
import { CAKE_SIZES, SUGAR_OPTIONS, INTERLAYER_OPTIONS, DECORATION_OPTIONS } from '@/lib/constants'
import { formatPrice } from '@/lib/utils'
import { Check, ChevronLeft, ChevronRight, ShoppingCart, Sparkles } from 'lucide-react'
import { toast } from 'sonner'
import Link from 'next/link'

const STEPS = ['选基底', '选尺寸', '选口味', '选夹层', '选装饰', '祝福语']

const BASE_CAKES = [
  { id: 'base1', name: '原味戚风', desc: '经典柔软', image: 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=200&h=200&fit=crop', price: 88 },
  { id: 'base2', name: '巧克力戚风', desc: '浓郁可可', image: 'https://images.unsplash.com/photo-1565958011703-44f9829ba187?w=200&h=200&fit=crop', price: 98 },
  { id: 'base3', name: '抹茶戚风', desc: '清新抹茶', image: 'https://images.unsplash.com/photo-1519869325930-281384150729?w=200&h=200&fit=crop', price: 98 },
  { id: 'base4', name: '红丝绒', desc: '浪漫红色', image: 'https://images.unsplash.com/photo-1588195538326-c5b1e9f80a1b?w=200&h=200&fit=crop', price: 108 },
  { id: 'base5', name: '榴莲戚风', desc: '榴莲风味', image: 'https://images.unsplash.com/photo-1621303837174-89787a7d4729?w=200&h=200&fit=crop', price: 128 },
]

const TASTE_OPTIONS = ['奶油霜', '淡奶油', '巧克力酱', '抹茶酱', '芝士霜', '水果奶油']

export default function CustomizePage() {
  const router = useRouter()
  const addItem = useCartStore((s) => s.addItem)

  const [step, setStep] = useState(0)
  const [selectedBase, setSelectedBase] = useState(0)
  const [selectedSize, setSelectedSize] = useState(1) // 默认8寸
  const [selectedTaste, setSelectedTaste] = useState(1) // 默认淡奶油
  const [selectedInterlayer, setSelectedInterlayer] = useState(0)
  const [selectedDecorations, setSelectedDecorations] = useState<string[]>([])
  const [customText, setCustomText] = useState('')

  const base = BASE_CAKES[selectedBase]
  const sizeMultiplier = CAKE_SIZES[selectedSize].priceMultiplier
  const decorationExtra = selectedDecorations.length * 15
  const textExtra = customText.trim() ? 10 : 0
  const totalPrice = Math.round((base.price * sizeMultiplier) + decorationExtra + textExtra)

  const toggleDecoration = (d: string) => {
    setSelectedDecorations(prev =>
      prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]
    )
  }

  const handleAddToCart = () => {
    addItem({
      cakeStyleId: -Date.now(),
      name: `定制蛋糕·${base.name}`,
      image: base.image,
      price: totalPrice,
      size: CAKE_SIZES[selectedSize].name,
      taste: TASTE_OPTIONS[selectedTaste],
      sugar: SUGAR_OPTIONS[2],
      interlayer: INTERLAYER_OPTIONS[selectedInterlayer],
      decoration: selectedDecorations.join('、'),
      customText: customText.trim() || undefined,
      quantity: 1,
      isCustom: true,
    })
    toast.success('定制蛋糕已加入购物车')
    router.push('/cart')
  }

  return (
    <div className="min-h-screen pb-20 bg-gray-50">
      <TopBar />

      {/* 步骤进度 */}
      <div className="sticky top-12 z-30 bg-white border-b border-gray-100 px-4 py-2">
        <div className="flex items-center justify-between">
          {STEPS.map((s, idx) => (
            <div key={s} className="flex items-center">
              <button onClick={() => idx < step && setStep(idx)}
                className={`w-6 h-6 rounded-full text-[10px] font-bold flex items-center justify-center transition-colors ${idx < step ? 'bg-pink-500 text-white' : idx === step ? 'bg-pink-500 text-white ring-2 ring-pink-200' : 'bg-gray-200 text-gray-400'}`}
              >
                {idx < step ? <Check className="w-3 h-3" /> : idx + 1}
              </button>
              {idx < STEPS.length - 1 && (
                <div className={`w-6 h-0.5 mx-0.5 ${idx < step ? 'bg-pink-500' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-1">
          {STEPS.map(s => (
            <span key={s} className="text-[9px] text-gray-400 flex-1 text-center">{s}</span>
          ))}
        </div>
      </div>

      <div className="px-4 mt-3">
        {/* Step 0: 选基底 */}
        {step === 0 && (
          <div>
            <h3 className="text-sm font-bold text-gray-800 mb-2">选择蛋糕基底</h3>
            <div className="space-y-2">
              {BASE_CAKES.map((b, idx) => (
                <button key={b.id} onClick={() => setSelectedBase(idx)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${selectedBase === idx ? 'border-pink-500 bg-pink-50' : 'border-gray-100 bg-white'}`}
                >
                  <div className="relative w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                    <Image src={b.image} alt={b.name} fill className="object-cover" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-bold text-gray-800">{b.name}</p>
                    <p className="text-[10px] text-gray-400">{b.desc}</p>
                    <p className="text-sm font-bold text-pink-500 mt-0.5">¥{b.price}起</p>
                  </div>
                  {selectedBase === idx && <Check className="w-5 h-5 text-pink-500 flex-shrink-0" />}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 1: 选尺寸 */}
        {step === 1 && (
          <div>
            <h3 className="text-sm font-bold text-gray-800 mb-2">选择尺寸</h3>
            <div className="grid grid-cols-2 gap-3">
              {CAKE_SIZES.map((size, idx) => (
                <button key={size.name} onClick={() => setSelectedSize(idx)}
                  className={`p-4 rounded-xl border-2 text-center transition-all ${selectedSize === idx ? 'border-pink-500 bg-pink-50' : 'border-gray-100 bg-white'}`}
                >
                  <p className="text-lg font-bold text-gray-800">{size.name}</p>
                  <p className="text-xs text-gray-500 mt-1">{size.description}</p>
                  <p className="text-sm font-bold text-pink-500 mt-1">{formatPrice(Math.round(base.price * size.priceMultiplier))}</p>
                  {selectedSize === idx && <Check className="w-5 h-5 text-pink-500 mx-auto mt-1" />}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: 选口味 */}
        {step === 2 && (
          <div>
            <h3 className="text-sm font-bold text-gray-800 mb-2">选择口味霜</h3>
            <div className="grid grid-cols-2 gap-3">
              {TASTE_OPTIONS.map((t, idx) => (
                <button key={t} onClick={() => setSelectedTaste(idx)}
                  className={`p-3 rounded-xl border-2 text-center transition-all ${selectedTaste === idx ? 'border-pink-500 bg-pink-50' : 'border-gray-100 bg-white'}`}
                >
                  <p className="text-sm font-bold text-gray-800">{t}</p>
                  {selectedTaste === idx && <Check className="w-4 h-4 text-pink-500 mx-auto mt-1" />}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: 选夹层 */}
        {step === 3 && (
          <div>
            <h3 className="text-sm font-bold text-gray-800 mb-2">选择夹层</h3>
            <div className="grid grid-cols-2 gap-3">
              {INTERLAYER_OPTIONS.map((inter, idx) => (
                <button key={inter} onClick={() => setSelectedInterlayer(idx)}
                  className={`p-3 rounded-xl border-2 text-center transition-all ${selectedInterlayer === idx ? 'border-pink-500 bg-pink-50' : 'border-gray-100 bg-white'}`}
                >
                  <p className="text-sm font-bold text-gray-800">{inter}</p>
                  {selectedInterlayer === idx && <Check className="w-4 h-4 text-pink-500 mx-auto mt-1" />}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 4: 选装饰 */}
        {step === 4 && (
          <div>
            <h3 className="text-sm font-bold text-gray-800 mb-2">选择装饰（可多选，每项+15元）</h3>
            <div className="grid grid-cols-2 gap-3">
              {DECORATION_OPTIONS.map((d) => (
                <button key={d} onClick={() => toggleDecoration(d)}
                  className={`p-3 rounded-xl border-2 text-center transition-all ${selectedDecorations.includes(d) ? 'border-pink-500 bg-pink-50' : 'border-gray-100 bg-white'}`}
                >
                  <p className="text-sm font-bold text-gray-800">{d}</p>
                  <p className="text-[10px] text-gray-400">+¥15</p>
                  {selectedDecorations.includes(d) && <Check className="w-4 h-4 text-pink-500 mx-auto mt-0.5" />}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 5: 祝福语 */}
        {step === 5 && (
          <div>
            <h3 className="text-sm font-bold text-gray-800 mb-2">自定义祝福语（+¥10）</h3>
            <textarea
              value={customText}
              onChange={(e) => setCustomText(e.target.value.slice(0, 30))}
              placeholder="生日快乐、永远爱你..."
              maxLength={30}
              className="w-full h-24 p-3 bg-white border-2 border-gray-100 rounded-xl text-sm outline-none focus:border-pink-300 resize-none"
            />
            <p className="text-[10px] text-gray-400 text-right mt-1">{customText.length}/30</p>

            {/* 定制预览 */}
            <div className="mt-4 p-4 bg-white rounded-xl border border-gray-100">
              <div className="flex items-center gap-1 mb-2">
                <Sparkles className="w-4 h-4 text-pink-500" />
                <span className="text-sm font-bold text-gray-800">定制预览</span>
              </div>
              <div className="text-xs text-gray-600 space-y-1">
                <p>基底：{base.name} · {CAKE_SIZES[selectedSize].name}</p>
                <p>口味：{TASTE_OPTIONS[selectedTaste]}</p>
                <p>夹层：{INTERLAYER_OPTIONS[selectedInterlayer]}</p>
                {selectedDecorations.length > 0 && <p>装饰：{selectedDecorations.join('、')}</p>}
                {customText.trim() && <p>祝福语：「{customText}」</p>}
              </div>
              <p className="text-lg font-bold text-pink-500 mt-2 text-right">{formatPrice(totalPrice)}</p>
            </div>
          </div>
        )}
      </div>

      {/* 底部操作 */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-100 safe-area-bottom">
        <div className="max-w-lg mx-auto flex items-center h-14 px-4 gap-3">
          {step > 0 && (
            <button onClick={() => setStep(step - 1)}
              className="flex items-center gap-1 px-3 h-10 border border-gray-200 rounded-full text-sm text-gray-600"
            >
              <ChevronLeft className="w-4 h-4" /> 上一步
            </button>
          )}
          <div className="flex-1 text-right">
            <span className="text-sm text-gray-400">合计 </span>
            <span className="text-lg font-bold text-pink-500">{formatPrice(totalPrice)}</span>
          </div>
          {step < STEPS.length - 1 ? (
            <button onClick={() => setStep(step + 1)}
              className="flex items-center gap-1 px-5 h-10 bg-pink-500 text-white text-sm font-bold rounded-full hover:bg-pink-600"
            >
              下一步 <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={handleAddToCart}
              className="flex items-center gap-1 px-5 h-10 bg-pink-500 text-white text-sm font-bold rounded-full hover:bg-pink-600 active:scale-95 transition-transform"
            >
              <ShoppingCart className="w-4 h-4" /> 加入购物车
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
